import React, { ChangeEventHandler, useCallback } from 'react';
import { useDispatch } from 'react-redux';
import { useDebounceCallback } from '@react-hook/debounce';

// @ts-ignore lib missing typings
import { addSearchedPhrase } from '@lootboy/lib/actions';

import SearchInput from '~/components/molecules/SearchInput';

import { IUseBooleanChangeValue } from '~/hooks/useBooleanState';
import { useOnClickOutsideWithRef } from '~/hooks/useOnClickOutside';
import { useSlotsContext } from '~/redux/slices/slotsFilters';

interface ISearchInputProps {
  changeInputFocused: IUseBooleanChangeValue;
}

const PHRASE_LENGTH_THRESHOLD = 2;

const SearchInputWrapper: React.FC<ISearchInputProps> = ({
  changeInputFocused: {
    setTrue: setInputFocusedTrue,
    setFalse: setInputFocusedFalse,
  },
}) => {
  const [setRef] =
    useOnClickOutsideWithRef<HTMLDivElement>(setInputFocusedFalse);

  const dispatch = useDispatch();
  const { useSearch } = useSlotsContext();
  const [searchValue, setSearchValue] = useSearch();

  const saveSearchPhrase = useCallback(
    (searchPhrase: string) => {
      if (searchPhrase.length > PHRASE_LENGTH_THRESHOLD) {
        dispatch(addSearchedPhrase(searchPhrase));
      }
    },
    [dispatch],
  );

  const debouncedSaveSearchPhrase = useDebounceCallback(saveSearchPhrase, 1000);

  const onInputChange: ChangeEventHandler<HTMLInputElement> = ({
    target: { value },
  }) => {
    setSearchValue(value);
    if (value?.trim()) {
      debouncedSaveSearchPhrase(value.trim());
    }
  };

  const onClearIconClick = useCallback(() => {
    setSearchValue('');
    setInputFocusedTrue();
  }, [setInputFocusedTrue, setSearchValue]);

  return (
    <SearchInput
      onClearIconClick={onClearIconClick}
      value={searchValue}
      onChange={onInputChange}
      onFocus={setInputFocusedTrue}
      ref={setRef}
    />
  );
};

export default SearchInputWrapper;
